// client/src/components/ticketing/TicketConfig.jsx
//
// Tenant-admin ticketing page for one event item: enable/disable ticketing,
// manage ticket tiers, and (via the two sub-panels) custom registration
// fields and promo codes. Analog of RaffleConfig.jsx, but talking to the new
// parallel ticketing tables/routes (server/routes/ticketingRoutes.js)
// instead of t_raffle.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import RegistrationFieldBuilder from "./RegistrationFieldBuilder";
import PromoCodeManager from "./PromoCodeManager";

const EMPTY_TIER = {
  name: "",
  tier_type: "",
  price: "0",
  currency: "usd",
  capacity: "",
  min_per_order: "1",
  max_per_order: "10",
};

const TABS = [
  { key: "tiers", label: "Ticket Tiers" },
  { key: "fields", label: "Registration Fields" },
  { key: "promo", label: "Promo Codes" },
];

export default function TicketConfig({ tenantId, eventId, itemId, itemName, onClose }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [activeTab, setActiveTab] = useState("tiers");
  const [message, setMessage] = useState({ text: "", type: "" });

  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState(EMPTY_TIER);
  const [editingTierId, setEditingTierId] = useState(null);
  const [savingTier, setSavingTier] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${base}/config`);
      setEnabled(!!data.success);
    } catch (err) {
      if (err?.response?.status === 404) {
        setEnabled(false);
      } else {
        setMessage({ text: "Failed to load ticketing configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  }, [base]);

  const loadTiers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/tiers`);
      if (data.success) setTiers(data.tiers);
    } catch (err) {
      console.error("Error loading ticket tiers:", err);
    }
  }, [base]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (enabled) loadTiers();
  }, [enabled, loadTiers]);

  const handleEnable = async () => {
    setEnabling(true);
    setMessage({ text: "", type: "" });
    try {
      const { data } = await axios.post(`${base}/config`, {});
      if (data.success) {
        setEnabled(true);
        setMessage({ text: data.message || "Ticketing enabled", type: "success" });
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to enable ticketing", type: "error" });
    } finally {
      setEnabling(false);
    }
  };

  const resetTierForm = () => {
    setTierForm(EMPTY_TIER);
    setEditingTierId(null);
  };

  const startEditTier = (tier) => {
    setEditingTierId(tier.id);
    setTierForm({
      name: tier.name || "",
      tier_type: tier.tier_type || "",
      price: String(tier.price ?? "0"),
      currency: tier.currency || "usd",
      capacity: tier.capacity != null ? String(tier.capacity) : "",
      min_per_order: String(tier.min_per_order ?? "1"),
      max_per_order: String(tier.max_per_order ?? "10"),
    });
  };

  const submitTier = async (e) => {
    e.preventDefault();
    setSavingTier(true);
    setMessage({ text: "", type: "" });

    const payload = {
      name: tierForm.name.trim(),
      tier_type: tierForm.tier_type.trim() || null,
      price: Number(tierForm.price),
      currency: tierForm.currency || "usd",
      capacity: tierForm.capacity === "" ? null : Number(tierForm.capacity),
      min_per_order: Number(tierForm.min_per_order) || 1,
      max_per_order: Number(tierForm.max_per_order) || 10,
    };

    try {
      const { data } = editingTierId
        ? await axios.put(`${base}/tiers/${editingTierId}`, payload)
        : await axios.post(`${base}/tiers`, payload);

      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        resetTierForm();
        loadTiers();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to save tier", type: "error" });
    } finally {
      setSavingTier(false);
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm("Delete this ticket tier? This cannot be undone.")) return;
    try {
      await axios.delete(`${base}/tiers/${tierId}`);
      loadTiers();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete tier", type: "error" });
    }
  };

  if (loading) {
    return <div className="p-6 text-sm opacity-70">Loading ticketing configuration…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100">
          Ticketing {itemName ? `— ${itemName}` : ""}
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border hover:bg-black/5 dark:hover:bg-white/10 text-sm"
          >
            Close
          </button>
        )}
      </div>

      {message.text && (
        <div
          className={`mb-4 px-3 py-2 rounded text-sm ${
            message.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {!enabled ? (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-6 text-center">
          <p className="mb-4 text-sm opacity-80">
            Ticketing isn't enabled for this event item yet. Enable it to configure ticket tiers,
            custom registration fields, and promo codes.
          </p>
          <button
            onClick={handleEnable}
            disabled={enabling}
            className="px-4 py-2 rounded bg-cobalt text-white font-medium disabled:opacity-50"
          >
            {enabling ? "Enabling…" : "Enable Ticketing"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4 border-b border-black/10 dark:border-white/10">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? "border-cobalt text-cobalt dark:text-brand-orange dark:border-brand-orange"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "tiers" && (
            <div>
              <form
                onSubmit={submitTier}
                className="rounded-xl border border-black/10 dark:border-white/10 p-4 mb-4 grid grid-cols-2 gap-3"
              >
                <input
                  className="col-span-2 px-3 py-2 rounded border bg-transparent"
                  placeholder="Tier name (e.g. Early Bird, VIP, Group)"
                  value={tierForm.name}
                  onChange={(e) => setTierForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
                <input
                  className="px-3 py-2 rounded border bg-transparent"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Price"
                  value={tierForm.price}
                  onChange={(e) => setTierForm((f) => ({ ...f, price: e.target.value }))}
                  required
                />
                <input
                  className="px-3 py-2 rounded border bg-transparent"
                  type="number"
                  min="0"
                  placeholder="Capacity (blank = unlimited)"
                  value={tierForm.capacity}
                  onChange={(e) => setTierForm((f) => ({ ...f, capacity: e.target.value }))}
                />
                <input
                  className="px-3 py-2 rounded border bg-transparent"
                  type="number"
                  min="1"
                  placeholder="Min per order"
                  value={tierForm.min_per_order}
                  onChange={(e) => setTierForm((f) => ({ ...f, min_per_order: e.target.value }))}
                />
                <input
                  className="px-3 py-2 rounded border bg-transparent"
                  type="number"
                  min="1"
                  placeholder="Max per order"
                  value={tierForm.max_per_order}
                  onChange={(e) => setTierForm((f) => ({ ...f, max_per_order: e.target.value }))}
                />
                <div className="col-span-2 flex gap-2">
                  <button
                    type="submit"
                    disabled={savingTier}
                    className="px-4 py-2 rounded bg-cobalt text-white text-sm font-medium disabled:opacity-50"
                  >
                    {savingTier ? "Saving…" : editingTierId ? "Update Tier" : "Add Tier"}
                  </button>
                  {editingTierId && (
                    <button
                      type="button"
                      onClick={resetTierForm}
                      className="px-4 py-2 rounded border text-sm"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {tiers.length === 0 ? (
                <div className="text-sm opacity-70">No ticket tiers yet. Add one above.</div>
              ) : (
                <ul className="space-y-2">
                  {tiers.map((tier) => (
                    <li
                      key={tier.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-black/10 dark:border-white/10 p-3 text-sm"
                    >
                      <div>
                        <div className="font-semibold">{tier.name}</div>
                        <div className="opacity-70">
                          {Number(tier.price).toFixed(2)} {String(tier.currency).toUpperCase()} ·{" "}
                          {tier.capacity == null ? "Unlimited" : `${tier.quantity_sold}/${tier.capacity} sold`}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditTier(tier)}
                          className="px-2 py-1 rounded border hover:bg-black/5 dark:hover:bg-white/10"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteTier(tier.id)}
                          className="px-2 py-1 rounded border hover:bg-black/5 dark:hover:bg-white/10 text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === "fields" && (
            <RegistrationFieldBuilder tenantId={tenantId} eventId={eventId} itemId={itemId} />
          )}

          {activeTab === "promo" && (
            <PromoCodeManager tenantId={tenantId} eventId={eventId} itemId={itemId} />
          )}
        </>
      )}
    </div>
  );
}
